// Core imports
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import moment from 'moment';

// Component imports
import { Button, Input, Row, Col, Form, DatePicker, InputNumber, Select, Checkbox, Radio } from 'antd';
import { RiCalendarLine, RiDeleteBin6Line } from 'react-icons/ri';

// Misc imports
import { BODY_FUNCTIONS, PREVIEW_MODES, STORAGE_KEYS } from '../../constants';

// Constant declarations
const DURATION_METRICS = {
  MINUTES: 'minutes',
  HOURS: 'hours',
  DAYS: 'days',
  YEARS: 'years'
};

const generateInitialValues = (initialValues) => {
  const deathDurationInMilliseconds = (initialValues?.deathDuration || 0) * 3600000; // Duration comes in hours from the server
  let deathDuration = moment.duration(deathDurationInMilliseconds);
  let deathDurationNumber = null;
  let deathDurationMetric = DURATION_METRICS.HOURS;

  if (deathDuration.asMilliseconds() > 0) {
    if (deathDuration.asYears() >= 1) {
      deathDurationMetric = DURATION_METRICS.YEARS;
      deathDurationNumber = deathDuration.asYears();
    } else if (deathDuration.asDays() >= 1) {
      deathDurationMetric = DURATION_METRICS.DAYS;
      deathDurationNumber = deathDuration.asDays();
    } else if (deathDuration.asHours() >= 1) {
      deathDurationMetric = DURATION_METRICS.HOURS;
      deathDurationNumber = deathDuration.asHours();
    } else if (deathDuration.asMinutes() >= 1) {
      deathDurationMetric = DURATION_METRICS.MINUTES;
      deathDurationNumber = deathDuration.asMinutes();
    }
  }

  const ndeDurationInMilliseconds = (initialValues?.ndeDuration || 0) * 3600000; // Duration comes in hours from the server
  let ndeDuration = moment.duration(ndeDurationInMilliseconds);
  let ndeDurationNumber = null;
  let ndeDurationMetric = DURATION_METRICS.HOURS;

  if (ndeDuration.asMilliseconds() > 0) {
    if (ndeDuration.asYears() >= 1) {
      ndeDurationMetric = DURATION_METRICS.YEARS;
      ndeDurationNumber = ndeDuration.asYears();
    } else if (ndeDuration.asDays() >= 1) {
      ndeDurationMetric = DURATION_METRICS.DAYS;
      ndeDurationNumber = ndeDuration.asDays();
    } else if (ndeDuration.asHours() >= 1) {
      ndeDurationMetric = DURATION_METRICS.HOURS;
      ndeDurationNumber = ndeDuration.asHours();
    } else if (ndeDuration.asMinutes() >= 1) {
      ndeDurationMetric = DURATION_METRICS.MINUTES;
      ndeDurationNumber = ndeDuration.asMinutes();
    }
  }

  if (initialValues) {
    return {
      ...initialValues,
      ...(initialValues.dateOfNde ? { dateOfNde: moment(initialValues.dateOfNde) } : { dateOfNde: null }),
      deathDurationMetric: initialValues?.deathDuration ? deathDurationMetric : DURATION_METRICS.HOURS,
      ndeDurationMetric: initialValues?.ndeDuration ? ndeDurationMetric : DURATION_METRICS.HOURS,
      ...(initialValues?.deathDuration && { deathDurationNumber }),
      ...(initialValues?.ndeDuration && { ndeDurationNumber })
    };
  }

  // Else
  const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEYS.NDE_TO_CREATE)) || {};
  return {
    deathDurationMetric: DURATION_METRICS.HOURS,
    ndeDurationMetric: DURATION_METRICS.HOURS,
    ...stored
  };
};

/**
 * Duration is sent as milliseconds
 * @param {String} durationMetrics
 * @returns An integer to set as the max value in number inputs
 */
const getMaxIntForDuration = (durationMetrics) => {
  switch (durationMetrics) {
    case DURATION_METRICS.MINUTES:
      return 60;
    case DURATION_METRICS.HOURS:
      return 24;
    case DURATION_METRICS.DAYS:
      return 365;
    case DURATION_METRICS.YEARS:
      return 10000;
    default:
      return Number.MAX_SAFE_INTEGER;
  }
};

const DeathInformationTab = props => {
  const { wrapperCol, mode, onSubmitSuccess, onStepChange, initialValues, onDelete } = props;
  const location = useLocation();
  const { pathname } = location;
  const [form] = Form.useForm();

  const [, updateState] = useState(); // Hook to force a component re-render

  // TODO: For fields that appear and disappear, make sure to clear the values if needed

  const processFormValues = (values) => {
    const { deathDurationNumber, deathDurationMetric, ndeDurationNumber, ndeDurationMetric, ...rest } = values;
    return {
      ...rest,
      deathDuration: (deathDurationNumber && deathDurationMetric)
        ? moment.duration(deathDurationNumber, deathDurationMetric).asHours()
        : null,
      ndeDuration: (ndeDurationNumber && ndeDurationMetric)
        ? moment.duration(ndeDurationNumber, ndeDurationMetric).asHours()
        : null
    };
  };

  // On mount
  useEffect(() => {
    form.resetFields();

    return () => {
      onStepChange(processFormValues(form.getFieldsValue(true)));
    };
  }, [pathname]);

  const onSubmit = (values) => {
    onSubmitSuccess(processFormValues(values));
  };

  return (
    <Form
      layout="vertical"
      name="deathInformationForm"
      form={form}
      initialValues={generateInitialValues(initialValues)}
      onFinish={onSubmit}
      onFieldsChange={(_, allFields) => updateState(allFields)}
      scrollToFirstError
    >
      <Row>
        <Col {...wrapperCol}>
          <Row className="mb-20">
            <Col lg={18} span={24} className="mb-32">
              <h3>Date and circumstances</h3>
              <p className="p1-body mb-0">
                We accept a broad range of profound experiences, including NDEs, OBEs (Out-of-Body Experiences), and STEs (Spiritually Transformative Experiences).
              </p>
            </Col>

            <Col span={24} className="mb-20">
              <h5 className="mb-20">
                When did your NDE happen?
              </h5>
              <Form.Item name="dateOfNde">
                <DatePicker
                  className="w-100"
                  size="large"
                  disabledDate={(current) => {
                    return current && current > moment().add(1, 'day').startOf('day');
                  }}
                  suffixIcon={<RiCalendarLine className="remix-icon text-color-black-60" />}
                />
              </Form.Item>
            </Col>

            <Col span={24} className="mb-20">
              <h5 className="mb-20">
                How long were you <strong><i>clinically dead</i></strong> for?
              </h5>

              <Form.Item name="deathDurationNumber">
                <InputNumber
                  placeholder="e.g. 5"
                  addonAfter={
                    <Form.Item name="deathDurationMetric" className="mb-0">
                      <Select>
                        <Select.Option value={DURATION_METRICS.MINUTES}>{DURATION_METRICS.MINUTES}</Select.Option>
                        <Select.Option value={DURATION_METRICS.HOURS}>{DURATION_METRICS.HOURS}</Select.Option>
                        <Select.Option value={DURATION_METRICS.DAYS}>{DURATION_METRICS.DAYS}</Select.Option>
                      </Select>
                    </Form.Item>
                  }
                  precision={0}
                  max={getMaxIntForDuration(form.getFieldValue('deathDurationMetric'))}
                  className="w-100 addon-with-form-item"
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <h5 className="mb-4">
                How long did you <strong><i>feel</i></strong> your NDE lasted?
              </h5>
              <p className="p1-body mb-20">
                It can be hard to specify how long your NDE lasted and experiences vary. Just give a rough estimate.
              </p>

              <Form.Item name="ndeDurationNumber">
                <InputNumber
                  placeholder="e.g. 5"
                  addonAfter={
                    <Form.Item name="ndeDurationMetric" className="mb-0">
                      <Select>
                        <Select.Option value={DURATION_METRICS.MINUTES}>{DURATION_METRICS.MINUTES}</Select.Option>
                        <Select.Option value={DURATION_METRICS.HOURS}>{DURATION_METRICS.HOURS}</Select.Option>
                        <Select.Option value={DURATION_METRICS.DAYS}>{DURATION_METRICS.DAYS}</Select.Option>
                        <Select.Option value={DURATION_METRICS.YEARS}>{DURATION_METRICS.YEARS}</Select.Option>
                      </Select>
                    </Form.Item>
                  }
                  precision={0}
                  max={getMaxIntForDuration(form.getFieldValue('ndeDurationMetric'))}
                  className="w-100 addon-with-form-item"
                />
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                What was the cause of your death?
              </h5>

              <Form.Item name="deathCause">
                <Input size="large" placeholder="Please describe (optional)" />
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-4">
                What were the circumstances of your death?
              </h5>
              <p className="p1-body mb-20">
                Choose all that apply.
              </p>

              <Form.Item name="bodyFunctions">
                <Checkbox.Group
                  className="checkbox-group-cards checkbox-group-cards-stretch"
                  options={[
                    { label: 'Breathing had stopped', value: BODY_FUNCTIONS.BREATHING_STOPPED },
                    { label: 'Heart had stopped', value: BODY_FUNCTIONS.HEART_STOPPED },
                    { label: 'Brain function had stopped', value: BODY_FUNCTIONS.BRAIN_STOPPED },
                    { label: 'I was in a coma', value: BODY_FUNCTIONS.COMA }
                  ]}
                >
                </Checkbox.Group>
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                How old were you at the time of your NDE?
              </h5>

              <Form.Item name="age">
                <Input size="large" placeholder="Your age at the time (optional)" />
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                Were you on any medication? If yes, what kind?
              </h5>

              <Form.Item name="medications">
                <Input size="large" placeholder="Drug names or just a description is fine (optional)" />
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                What country are you from?
              </h5>

              <Form.Item name="country">
                <Input size="large" placeholder="Country (optional)" />
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                What is your ethnicity or cultural background?
              </h5>

              <Form.Item name="ethnicity">
                <Input size="large" placeholder="Ethnicity (optional)" />
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                What was your occupation at the time of your experience?
              </h5>

              <Form.Item name="occupation">
                <Input size="large" placeholder="Occupation (optional)" />
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                Were you actively resuscitated (e.g., CPR, defibrillation)?
              </h5>

              <Form.Item name="wasResuscitated">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                Were you under general anesthesia at the time of your experience?
              </h5>

              <Form.Item name="hadAnesthesia">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      {
        mode === PREVIEW_MODES.VIEW ? null : (
          <Row justify={mode === PREVIEW_MODES.EDIT ? 'space-between' : 'end'} align="middle" className="mt-48">
            {
              mode === PREVIEW_MODES.EDIT ? (
                <Col>
                  <Button
                    danger
                    icon={<RiDeleteBin6Line size={14} style={{ marginRight: 4 }} />}
                    onClick={onDelete}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    Delete NDE
                  </Button>
                </Col>
              ) : null
            }
            <Col>
              <Button type="primary" htmlType="submit" className="bg-black-100 border-color-black-100 hover-bg-black-80 hover-border-color-black-80">
                {
                  mode === PREVIEW_MODES.EDIT ? 'Save' : 'Next step'
                }
              </Button>
            </Col>
          </Row>
        )
      }
    </Form>
  );
};

DeathInformationTab.propTypes = {
  mode: PropTypes.string,
  wrapperCol: PropTypes.object,
  onSubmitSuccess: PropTypes.func,
  onStepChange: PropTypes.func,
  initialValues: PropTypes.object,
  onDelete: PropTypes.func
};

export default DeathInformationTab;
